'use client';

import CommonLoading from '@/components/layouts/CommonLoading/CommonLoading';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import type { UserDesignPrint } from '@/features/gfUserDesign/types';
import { sendClickShareDesignNavigationEvent } from '@/utils/ga';
import { getImageLayoutSizes } from '@/utils/nextImage';
import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from 'react';
import styles from './DesignPreviewImage.module.scss';

type DesignPreviewImageProps = {
  /** ユーザデザインのプリントエリア情報 */
  userPrints: UserDesignPrint[];
  workName: string;
  onImageClick: (imageUrl: string) => void;
  /** どのデザインが壊れているかを Sentry 通知で特定するための識別子 */
  userPrintId?: string | number;
};

/**
 * デザインのプレビュー画像
 * 親幅いっぱいに広がる（fill）。表示サイズは親コンテナで制御する。
 * 画像が複数ある場合はテキストボタンで画像を切り替え可能
 */
function DesignPreviewImage({ userPrints, workName, onImageClick, userPrintId }: DesignPreviewImageProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * プリントエリア切り替えボタンクリック時
   * 画像を前から順に切り替える。最後の画像の場合は最初に戻る（循環）
   */
  const handleSwitchPrintArea = () => {
    sendClickShareDesignNavigationEvent();
    setIsLoading(true);
    setCurrentIndex((prev) => {
      const nextIndex = prev + 1;
      return nextIndex >= userPrints.length ? 0 : nextIndex;
    });
  };

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  // prints は API の designPrints（プレビュー画像がある印刷エリアのみ）に対応する。
  // 全エリアのプレビューが未生成の異常データだと空配列で渡り、以降の要素アクセス
  // （currentPrint / userPrints[nextIndex]）で undefined 参照になり落ちるため描画しない。
  const isPrintsEmpty = userPrints.length === 0;

  // 空はデザイン保存時にプレビューが生成されなかった異常データを指す。握り潰すと発生を追えないため通知する。
  useEffect(() => {
    if (isPrintsEmpty) {
      Sentry.captureMessage('DesignPreviewImage に空の userPrints が渡された（プレビュー未生成のデザイン）', {
        level: 'warning',
        extra: { workName, userPrintId },
      });
    }
  }, [isPrintsEmpty, workName, userPrintId]);

  if (isPrintsEmpty) {
    return null;
  }

  const showPrintAreaSwitchButton = userPrints.length > 1;
  const currentPrint = userPrints[currentIndex];
  const nextIndex = (currentIndex + 1) % userPrints.length;
  const switchButtonLabel = `${userPrints[nextIndex].name}を表示`;

  return (
    <div className={`${styles.previewImageContainer} ${styles.fillPreviewImageContainer}`}>
      <button
        type='button'
        className={styles.imageClickTarget}
        onClick={() => onImageClick(currentPrint.previewImageUrl)}
        aria-label='画像を拡大'
      >
        <NextImageView
          src={currentPrint.previewImageUrl}
          alt={workName}
          fill={true}
          objectFit='contain'
          sizes={getImageLayoutSizes({ sp: '300px', pc: '400px' })}
          onLoad={handleLoadingComplete}
        />
      </button>
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <CommonLoading />
        </div>
      )}
      {showPrintAreaSwitchButton && (
        <button
          type='button'
          className={styles.printAreaSwitchButton}
          onClick={handleSwitchPrintArea}
          aria-label={switchButtonLabel}
        >
          {switchButtonLabel}
        </button>
      )}
    </div>
  );
}

export default DesignPreviewImage;
